
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, Save, Upload, RefreshCw, AlertTriangle, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ImageEditorProps {
  onSaveToGallery?: (imageUrl: string) => void;
}

const ImageEditor = ({ onSaveToGallery }: ImageEditorProps) => {
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [editedImage, setEditedImage] = useState<string | null>(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editResponse, setEditResponse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const dataURLToBlob = async (dataURL: string): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get 2D context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0);
          
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          }, 'image/png');
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image from data URL'));
        };
        
        img.src = dataURL;
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setSourceImage(e.target.result as string);
        setEditedImage(null);
        setEditResponse(null);
        setError(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleEditImage = async () => {
    if (!sourceImage || !editPrompt.trim()) {
      toast({
        title: "Error",
        description: "Please upload an image and enter an edit prompt",
        variant: "destructive",
      });
      return;
    }

    setIsEditing(true);
    setError(null);
    
    try {
      console.log("Calling edit-image function with prompt:", editPrompt);
      
      const { data, error } = await supabase.functions.invoke('edit-image', {
        body: { 
          imageBase64: sourceImage,
          editPrompt: editPrompt
        }
      });

      if (error) {
        console.error("Supabase function error:", error);
        throw new Error(error.message || "Error communicating with the image editing service");
      }
      
      console.log("Received response:", data);

      if (data?.error) {
        throw new Error(data.details || "Error editing image");
      }

      setEditResponse(data?.text || null);
      setEditedImage(data?.image || null);
      
      if (data?.image) {
        toast({
          title: "Image Edited Successfully",
          description: "Your image has been edited based on your instructions.",
        });
      } else {
        toast({
          title: "Processing Complete",
          description: "The AI has processed your request but couldn't generate an edited image.",
        });
      }
      
    } catch (error) {
      console.error("Error editing image:", error);
      setError(error instanceof Error ? error.message : "Failed to edit image");
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to edit image",
        variant: "destructive",
      });
    } finally {
      setIsEditing(false);
    }
  };

  const handleSaveToGallery = async () => {
    if (!editedImage) {
      toast({
        title: "Error",
        description: "No edited image to save",
        variant: "destructive",
      });
      return;
    }

    try {
      let publicUrl = "";
      
      if (editedImage.startsWith('data:')) {
        // Using Canvas API to convert data URL to blob
        const blob = await dataURLToBlob(editedImage);
        const fileName = `edited-${Date.now()}.png`;
        
        const { error: uploadError } = await supabase.storage
          .from('article-images')
          .upload(fileName, blob, {
            contentType: 'image/png'
          });

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from('article-images')
          .getPublicUrl(fileName);
          
        publicUrl = data.publicUrl;
      } else {
        publicUrl = editedImage;
      }

      toast({
        title: "Success",
        description: "Edited image saved to gallery successfully",
      });

      if (onSaveToGallery) {
        onSaveToGallery(publicUrl);
      }
    } catch (error) {
      console.error("Error saving to gallery:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save to gallery",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">Image Editor</h2>
          
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Upload an image and describe the changes you'd like. The AI will try to edit your image based on your instructions.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Upload Image to Edit</label>
              <p className="text-sm text-gray-500">
                Upload an image that you want to edit using AI (max 5MB)
              </p>
            </div>
            
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 text-gray-500 mb-2" />
                  <p className="text-sm text-gray-500">
                    Click to upload or drag and drop
                  </p>
                </div>
                <Input
                  id="edit-image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={isEditing}
                />
              </label>
            </div>
            
            {sourceImage && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Edit Instructions</label>
                  <Textarea
                    placeholder="Describe what changes you want to make to the image, e.g., 'Add a farmer next to the tractor' or 'Change the background to a sunset'"
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    disabled={isEditing}
                    className="min-h-[100px] resize-none"
                  />
                </div>
                
                <Button 
                  onClick={handleEditImage} 
                  disabled={isEditing || !editPrompt.trim()}
                  className="w-full"
                >
                  {isEditing ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Editing Image...
                    </>
                  ) : (
                    <>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit with AI
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sourceImage && (
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-medium mb-3">Original Image</h3>
              <div className="aspect-square relative rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={sourceImage}
                  alt="Original image"
                  className="w-full h-full object-contain"
                />
              </div>
            </CardContent>
          </Card>
        )}
        
        {(editedImage || editResponse) && (
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-medium mb-3">Result</h3>
              
              {editedImage ? (
                <div className="aspect-square relative rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={editedImage}
                    alt="AI edited image"
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="aspect-square relative rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center bg-gray-50">
                  <div className="text-center p-6">
                    <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
                    <p className="text-gray-600">No edited image was generated</p>
                  </div>
                </div>
              )}
              
              {editResponse && (
                <div className="mt-3 p-3 bg-gray-50 rounded-md text-sm">
                  <p className="font-medium">AI Response:</p>
                  <p className="text-gray-600 whitespace-pre-line">{editResponse}</p>
                </div>
              )}
              
              {editedImage && (
                <Button
                  variant="outline"
                  className="w-full mt-3"
                  onClick={handleSaveToGallery}
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save to Gallery
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ImageEditor;
